import React, { useState } from 'react';
import './EKSWizard.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

interface EKSWizardProps {
  onClose: () => void;
  onSuccess: () => void;
}

const EKSWizard: React.FC<EKSWizardProps> = ({ onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Step 1 data
  const [step1Data, setStep1Data] = useState({
    clusterName: '',
    accessLevel: 'Admin',
    region: 'us-east-1'
  });
  const [cloudFormationTemplate, setCloudFormationTemplate] = useState('');
  const [templateData, setTemplateData] = useState<any>(null);

  // Step 2 data
  const [step2Data, setStep2Data] = useState({
    roleArn: '',
    externalId: '',
    awsAccountId: ''
  });

  // Step 3 data
  const [step3Data, setStep3Data] = useState({
    name: ''
  });

  const [clusterInfo, setClusterInfo] = useState<any>(null);

  // Step 1: Get CloudFormation Template
  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${API_BASE_URL}/api/accounts/eks/cloudformation-template?clusterName=${encodeURIComponent(step1Data.clusterName)}&accessLevel=${step1Data.accessLevel}&automated=true`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (response.ok) {
        setCloudFormationTemplate(data.template);
        setTemplateData(data);
        setSuccessMessage('CloudFormation template generated successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.message || 'Failed to get CloudFormation template');
      }
    } catch (error) {
      setError('Network error: Unable to fetch template');
    } finally {
      setLoading(false);
    }
  };

  const handleNext1 = () => {
    if (cloudFormationTemplate && step1Data.clusterName) {
      setCurrentStep(2);
      setError(null);
    } else {
      setError('Please generate the CloudFormation template first');
    }
  };

  // Step 2: Create Access Entry
  const handleStep2Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const token = localStorage.getItem('auth_token');
      console.log('🔧 Creating EKS access entry via backend...');

      const response = await fetch(`${API_BASE_URL}/api/accounts/eks/create-access-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          clusterName: step1Data.clusterName,
          region: step1Data.region,
          roleArn: step2Data.roleArn,
          externalId: step2Data.externalId,
          awsAccountId: step2Data.awsAccountId,
          accessLevel: step1Data.accessLevel
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ EKS access entry created successfully');
        setClusterInfo(data.clusterInfo);
        setSuccessMessage(data.message);
        setTimeout(() => {
          setSuccessMessage(null);
          setCurrentStep(3);
        }, 2000);
      } else {
        setError(data.message || 'Failed to create EKS access entry');
      }
    } catch (error) {
      console.error('❌ Error creating access entry:', error);
      setError('Network error: Unable to create access entry');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Register Cluster
  const handleStep3Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      console.log('📝 Registering EKS cluster...');

      const response = await fetch(`${API_BASE_URL}/api/accounts/eks/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: step3Data.name,
          clusterName: step1Data.clusterName,
          region: step1Data.region,
          awsAccountId: step2Data.awsAccountId,
          roleArn: step2Data.roleArn,
          externalId: step2Data.externalId,
          accessLevel: step1Data.accessLevel
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ EKS cluster registered successfully');
        setSuccessMessage(`Cluster "${step3Data.name}" registered successfully!`);
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(data.message || 'Failed to register EKS cluster');
      }
    } catch (error) {
      console.error('❌ Error registering cluster:', error);
      setError('Network error: Unable to register cluster');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="eks-wizard-overlay">
      <div className="eks-wizard">
        <div className="wizard-header">
          <h2>🚀 Add EKS Cluster - Step {currentStep} of 3</h2>
          <button onClick={onClose} className="close-btn">✕</button>
        </div>

        <div className="wizard-progress">
          <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
            <div className="step-circle">1</div>
            <div className="step-label">Deploy CloudFormation</div>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
            <div className="step-circle">2</div>
            <div className="step-label">Create Access Entry</div>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
            <div className="step-circle">3</div>
            <div className="step-label">Register Cluster</div>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}

        {/* Step 1: CloudFormation */}
        {currentStep === 1 && (
          <div className="wizard-step">
            <h3>Step 1: Deploy CloudFormation Template</h3>
            <form onSubmit={handleStep1Submit}>
              <div className="form-group">
                <label>EKS Cluster Name:</label>
                <input
                  type="text"
                  value={step1Data.clusterName}
                  onChange={(e) => setStep1Data({ ...step1Data, clusterName: e.target.value })}
                  required
                  placeholder="e.g., infra-eks-poc"
                />
                <small>The actual name of your EKS cluster in AWS</small>
              </div>

              <div className="form-group">
                <label>AWS Region:</label>
                <select
                  value={step1Data.region}
                  onChange={(e) => setStep1Data({ ...step1Data, region: e.target.value })}
                >
                  <option value="us-east-1">us-east-1 (N. Virginia)</option>
                  <option value="us-west-2">us-west-2 (Oregon)</option>
                  <option value="eu-west-1">eu-west-1 (Ireland)</option>
                  <option value="ap-southeast-1">ap-southeast-1 (Singapore)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Access Level:</label>
                <select
                  value={step1Data.accessLevel}
                  onChange={(e) => setStep1Data({ ...step1Data, accessLevel: e.target.value })}
                >
                  <option value="Admin">Admin (Recommended)</option>
                  <option value="ClusterAdmin">Cluster Admin (Full Access)</option>
                  <option value="Edit">Edit (No RBAC)</option>
                  <option value="View">View (Read-Only)</option>
                </select>
                <small>Admin level is recommended for most use cases</small>
              </div>

              <button type="submit" disabled={loading} className="primary-btn">
                {loading ? 'Generating...' : '📄 Get CloudFormation Template'}
              </button>
            </form>

            {cloudFormationTemplate && (
              <div className="template-section">
                <h4>✅ CloudFormation Template Ready</h4>
                <p>Click the button below to deploy this template directly to your AWS account.</p>

                <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      // Get user ID for personalization
                      let userId = 'user';
                      try {
                        const token = localStorage.getItem('auth_token');
                        if (token) {
                          const payload = JSON.parse(atob(token.split('.')[1]));
                          userId = payload.id || payload.userId || 'user';
                        }
                      } catch (error) {
                        console.log('Using default user ID');
                      }

                      // Use the fixed S3 URL for EKS template
                      const templateUrl = 'https://s3.amazonaws.com/soc.gg/cloudformation/eks/eks-access-entry-template-automated.yaml';

                      // Generate unique stack name with timestamp suffix
                      const uniqueSuffix = Date.now().toString().slice(-6);
                      const stackName = `DevOpsX-EKS-${step1Data.clusterName.replace(/[^a-zA-Z0-9-]/g, '-')}-${uniqueSuffix}`;

                      // Create CloudFormation console URL with parameters
                      const deployUrl = `https://console.aws.amazon.com/cloudformation/home?region=${step1Data.region}#/stacks/create/review` +
                        `?templateURL=${encodeURIComponent(templateUrl)}` +
                        `&stackName=${encodeURIComponent(stackName)}` +
                        `&param_ClusterName=${encodeURIComponent(step1Data.clusterName)}` +
                        `&param_DevOpsXPrincipalArn=${encodeURIComponent('arn:aws:iam::195275632101:role/DevOpsX-Backend-Service-Role')}` +
                        `&param_AccessType=EC2_LINUX` +
                        `&param_AccessPolicyLevel=${encodeURIComponent(step1Data.accessLevel)}` +
                        `&param_UserIdentifier=${encodeURIComponent(userId)}` +
                        `&param_AccountName=${encodeURIComponent(`eks-${step1Data.clusterName}`)}`;

                      window.open(deployUrl, '_blank');
                    }}
                    className="primary-btn"
                    style={{ flex: 1 }}
                  >
                    🚀 Deploy to AWS
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      // Generate the same unique suffix for preview
                      const uniqueSuffix = Date.now().toString().slice(-6);
                      const previewStackName = `DevOpsX-EKS-${step1Data.clusterName.replace(/[^a-zA-Z0-9-]/g, '-')}-${uniqueSuffix}`;

                      // Show template in a new window for preview
                      const modal = window.open('', '_blank');
                      if (modal) {
                        modal.document.write(`
                          <html>
                          <head><title>EKS CloudFormation Template</title></head>
                          <body style="font-family: system-ui; margin: 0; padding: 20px; background: #f5f5f5;">
                            <div style="max-width: 1200px; margin: 0 auto; background: white; padding: 24px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                              <h2 style="margin-top: 0;">EKS CloudFormation Template Preview</h2>
                              <div style="background: #f8f9fa; padding: 12px; border-radius: 4px; margin-bottom: 16px;">
                                <p style="margin: 4px 0;"><strong>Stack Name:</strong> ${previewStackName}</p>
                                <p style="margin: 4px 0;"><strong>Region:</strong> ${step1Data.region}</p>
                                <p style="margin: 4px 0;"><strong>Cluster:</strong> ${step1Data.clusterName}</p>
                                <p style="margin: 4px 0;"><strong>Access Level:</strong> ${step1Data.accessLevel}</p>
                              </div>
                              <pre style="background: #1e1e1e; color: #d4d4d4; padding: 16px; border-radius: 6px; overflow-x: auto; font-size: 13px; line-height: 1.5;">${cloudFormationTemplate.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
                              <div style="margin-top: 16px; display: flex; gap: 12px;">
                                <button onclick="navigator.clipboard.writeText(\`${cloudFormationTemplate.replace(/`/g, '\\`')}\`); this.textContent='✅ Copied!';" style="background: #10b981; color: white; padding: 10px 20px; border: none; border-radius: 6px; cursor: pointer; font-weight: 500;">📋 Copy Template</button>
                                <button onclick="window.close();" style="background: #6b7280; color: white; padding: 10px 20px; border: none; border-radius: 6px; cursor: pointer; font-weight: 500;">Close</button>
                              </div>
                            </div>
                          </body>
                          </html>
                        `);
                      }
                    }}
                    className="secondary-btn"
                    style={{ flex: 1 }}
                  >
                    👁️ Preview Template
                  </button>
                </div>

                <div className="info-box" style={{ marginBottom: '20px' }}>
                  <strong>📋 Next Steps:</strong>
                  <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>Click "Deploy to AWS" to open the CloudFormation console</li>
                    <li>Review the stack parameters and click "Create stack"</li>
                    <li>Wait for the stack to complete (usually 2-3 minutes)</li>
                    <li>Copy the <strong>Role ARN</strong>, <strong>External ID</strong>, and <strong>Account ID</strong> from the stack Outputs tab</li>
                    <li>Come back here and click "Next" to continue</li>
                  </ol>
                </div>

                <div className="step-actions">
                  <button onClick={handleNext1} className="primary-btn">
                    Next: Enter Stack Outputs →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Enter CF Outputs & Create Access Entry */}
        {currentStep === 2 && (
          <div className="wizard-step">
            <h3>Step 2: Enter CloudFormation Outputs & Create Access Entry</h3>
            <p>After deploying the CloudFormation stack, copy the values from the <strong>Outputs</strong> tab:</p>

            <form onSubmit={handleStep2Submit}>
              <div className="form-group">
                <label>Role ARN:</label>
                <input
                  type="text"
                  value={step2Data.roleArn}
                  onChange={(e) => setStep2Data({ ...step2Data, roleArn: e.target.value })}
                  required
                  placeholder="arn:aws:iam::123456789012:role/DevOpsX-EKS-..."
                />
                <small>Copy from CloudFormation stack outputs</small>
              </div>

              <div className="form-group">
                <label>External ID:</label>
                <input
                  type="text"
                  value={step2Data.externalId}
                  onChange={(e) => setStep2Data({ ...step2Data, externalId: e.target.value })}
                  required
                  placeholder="devopsx-userid-clustername"
                />
                <small>Copy from CloudFormation stack outputs</small>
              </div>

              <div className="form-group">
                <label>AWS Account ID:</label>
                <input
                  type="text"
                  value={step2Data.awsAccountId}
                  onChange={(e) => setStep2Data({ ...step2Data, awsAccountId: e.target.value })}
                  required
                  placeholder="123456789012"
                  pattern="[0-9]{12}"
                />
                <small>12-digit AWS account ID from stack outputs</small>
              </div>

              <div className="info-box">
                <strong>ℹ️ What happens next:</strong>
                <p>We'll use these credentials to automatically:</p>
                <ul>
                  <li>✅ Assume the IAM role via AWS STS</li>
                  <li>✅ Create the EKS access entry</li>
                  <li>✅ Associate the {step1Data.accessLevel} access policy</li>
                  <li>✅ Verify the cluster is accessible</li>
                </ul>
                <p><strong>No manual AWS CLI commands needed!</strong></p>
              </div>

              <div className="step-actions">
                <button type="button" onClick={() => setCurrentStep(1)} className="secondary-btn">
                  ← Back
                </button>
                <button type="submit" disabled={loading} className="primary-btn">
                  {loading ? 'Creating Access Entry...' : '🔧 Create Access Entry Automatically'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 3: Register */}
        {currentStep === 3 && (
          <div className="wizard-step">
            <h3>Step 3: Register Cluster in DevOpsX</h3>

            {clusterInfo && (
              <div className="success-box">
                <h4>✅ EKS Access Entry Created Successfully!</h4>
                <p><strong>Cluster:</strong> {step1Data.clusterName}</p>
                {clusterInfo.version && <p><strong>Version:</strong> {clusterInfo.version}</p>}
                {clusterInfo.status && <p><strong>Status:</strong> {clusterInfo.status}</p>}
              </div>
            )}

            <form onSubmit={handleStep3Submit}>
              <div className="form-group">
                <label>Display Name for this Cluster:</label>
                <input
                  type="text"
                  value={step3Data.name}
                  onChange={(e) => setStep3Data({ ...step3Data, name: e.target.value })}
                  required
                  placeholder="e.g., Production EKS Cluster"
                />
                <small>A friendly name to identify this cluster in DevOpsX</small>
              </div>

              <div className="info-box">
                <strong>📊 You'll be able to:</strong>
                <ul>
                  <li>View pods, deployments, services, and nodes</li>
                  <li>Monitor cluster health and metrics</li>
                  <li>Access logs and troubleshoot issues</li>
                  <li>Manage workloads through AI chat interface</li>
                </ul>
              </div>

              <div className="step-actions">
                <button type="button" onClick={() => setCurrentStep(2)} className="secondary-btn">
                  ← Back
                </button>
                <button type="submit" disabled={loading} className="primary-btn">
                  {loading ? 'Registering...' : '✅ Register Cluster'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default EKSWizard;
